import { Stack, Box } from '@mui/material';
import { Button, colors, IconButton, Typography } from '@sweep-io/sweep-design';
import { Close } from '@sweep-io/sweep-design/dist/icons';
import { PromptGroup } from '@server/prompts';
import { SearchInput } from '../../../common/SearchInput';
import { ClampedText } from '../../../common/ClampedText';
import { useMemo, useState } from 'react';
import { DatePickerWrapper } from '../../../common/datepicker/DatePickerWrapper';
import {
  filterPromptsByDate,
  getPromptsEmptyState,
  getUserFilterItems,
  promptTableColumns,
} from './rightPanelHelpers';
import { PromptLibraryIcon } from '../PromptLibraryIcon';
import { usePromptsTable } from './usePromptsTable';
import { PromptsTable } from './PromptsTable';
import { PromptsViewLayout } from './PromptsViewLayout';
import { PromptWithCategory } from '../types';
import {
  AdvancedFilter,
  AdvancedFilterItem,
} from '../../../common/filters/advanced-filter/AdvancedFilter';
import { useSelector } from 'react-redux';
import { selectAccountUsers } from '../../../../reducers/accountUsersReducer';

interface CategoryViewProps {
  prompts: PromptWithCategory[];
  category: PromptGroup;
  onClose: () => void;
  onCreatePrompt: () => void;
  onPromptClick: (prompt: PromptWithCategory) => void;
  onDeletePrompt: (promptId: string) => void;
}

export const CategoryView = ({
  prompts,
  category,
  onClose,
  onCreatePrompt,
  onPromptClick,
  onDeletePrompt,
}: CategoryViewProps) => {
  const [selectedUserFilters, setSelectedUserFilters] = useState<string[]>([]);
  const accountUsers = useSelector(selectAccountUsers);

  const {
    searchTerm,
    setSearchTerm,
    setDateFilter,
    dateFilter,
    expandedRowId,
    setExpandedRowId,
    rows,
  } = usePromptsTable({
    prompts,
    onEditPrompt: onPromptClick,
    onUseInChatSuccess: onClose,
    onDeletePrompt,
    selectedCategoryFilters: [category.id],
    selectedUserFilters,
  });

  const hasAnyPrompts = prompts.length > 0;

  const columns = useMemo(
    () => promptTableColumns.filter((col) => col.field !== 'categoryName'),
    [],
  );

  const userFilterItems: AdvancedFilterItem[] = useMemo(() => {
    const dateFilteredPrompts = filterPromptsByDate(prompts, dateFilter);
    return getUserFilterItems(dateFilteredPrompts, accountUsers);
  }, [accountUsers, prompts, dateFilter]);

  const { title: emptyStateTitle, description: emptyStateDescription } = useMemo(
    () =>
      getPromptsEmptyState({
        hasPrompts: prompts.length > 0,
        hasActiveFilters:
          searchTerm.trim().length > 0 || selectedUserFilters.length > 0 || Boolean(dateFilter),
      }),
    [dateFilter, prompts.length, searchTerm, selectedUserFilters.length],
  );

  return (
    <PromptsViewLayout
      header={
        <Stack
          sx={{
            padding: 3,
            pb: 0,
            flexShrink: 0,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ pb: 3, borderBottom: `1px solid ${colors.grey[300]}` }}
          >
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" gap={1} alignItems="center" mb={1} minHeight="40px">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PromptLibraryIcon icon={category.iconName} size="large" />
                </Box>
                <Typography variant="h3-medium">{category.name}</Typography>
              </Stack>
              {category.description && (
                <Stack direction="row" gap={1} alignItems="center" pl={4} pr={4}>
                  <ClampedText
                    text={category.description}
                    typographyVariant="h4"
                    textColor={colors.grey[700]}
                    lines={3}
                  />
                </Stack>
              )}
            </Box>
            <Stack direction="row" gap={1} alignItems="center">
              <Button startIconName="Plus" size="medium" onClick={onCreatePrompt}>
                New Prompt
              </Button>
              <IconButton variant="flat" size="small" onClick={onClose}>
                <Close />
              </IconButton>
            </Stack>
          </Stack>
        </Stack>
      }
      filters={
        <Stack sx={{ padding: 3, paddingTop: 4, paddingBottom: 4, flexShrink: 0 }}>
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="body" color={colors.grey[800]}>
              Filters:
            </Typography>
            <DatePickerWrapper
              onSelectDate={setDateFilter}
              placeholder="Last modified"
              showClearButton
              disabled={!hasAnyPrompts}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            />
            <AdvancedFilter
              disabled={!hasAnyPrompts}
              texts={{
                title: 'Last modified by',
              }}
              items={userFilterItems}
              selectedItems={selectedUserFilters}
              onSelectedItemsChange={(items) => setSelectedUserFilters(items)}
            />
            <Box sx={{ flex: 1 }} />
            <Box sx={{ width: '300px' }}>
              <SearchInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClearButtonClick={() => setSearchTerm('')}
                placeholder="Search Prompts"
                withFixedMagnifyingGlassIcon
                TextFieldProps={{ value: searchTerm }}
              />
            </Box>
          </Stack>
        </Stack>
      }
    >
      <PromptsTable
        rows={rows}
        columns={columns}
        expandedRowId={expandedRowId}
        setExpandedRowId={setExpandedRowId}
        emptyStateTitle={emptyStateTitle}
        emptyStateDescription={emptyStateDescription}
      />
    </PromptsViewLayout>
  );
};
